import { parseNum } from "./common-js";

export function isIOS() {
  const hasAndroid = /Android/i.test(navigator.userAgent)
  if (hasAndroid) {
    return false
  }
  const hasIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent)
  return hasIOS
}

/**
 * 获取随机字符串
 * @param {number} length 字符串长度
 * @param {string} [type] 随机字符串类型，letter纯字母，number纯数字，mix字母与数字混合
 * @param {string} [prefix] 字符串前缀
 * @returns {string} 生成的随机字符串
 */
export function randomString(length: number, type?: string, prefix?: string): string {
  if (type === undefined) {
    type = 'letter'
  }
  if (prefix === undefined) {
    prefix = ''
  }
  const numbers = '0123456789'
  const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz'
  let chars = ''
  let result = prefix
  if (type === 'number' || type === 'mix') {
    chars += numbers
  }
  if (type === 'letter' || type === 'mix') {
    chars += letters
  }
  for (let i = 0; i < length; i++) {
    result += chars[Math.floor(Math.random() * chars.length)]
  }
  return result
}




/**
 * json 格式转为 路由query 格式
 * { a: 1, b: 2} => '?a=1&b=2'
 * @param {Object} json 
 * @returns {String} 
 */
export const json2query = (json = {}) => {
  let query = ''
  Object.entries(json).forEach(([key, val]) => {
    const str = `${key}=${val}`
    query = query + '&' + str
  })
  return query.replace(/^&/, '?')
}

/**
 * 路径解参
 */
export const parsePathQuery = (pathStr: string) => {
  const [ path = '', queryStr = '' ] = pathStr.split('?')
  const query: Record<string, string> = {}
  queryStr.split('&').forEach(keyValue => {
    const [key='', value=''] = keyValue.split('=')
    query[key] = value
  })
  return {
    path,
    query,
  }
}

/**
 * 路径带参格式化
 */
export const formatPathQuery = (pathStr: string, queryJson: Record<string, any>) => {
  let { path, query: pathJson } = parsePathQuery(pathStr)
  pathJson = {
    ...pathJson,
    ...queryJson,
  }
  let isFirst = true
  Object.entries(pathJson).forEach(([key, val]) => {
    if (key) {
      const keyValue = `${key}=${val}`
      const token = isFirst ? '?' : '&'
      isFirst = false
      path = `${path}${token}${keyValue}`
    }
  })
  return path
}

/**
 * 格式化百分比保留小数点n位并千位逗号
 * @param {number} value 要处理的数值
 * @param {number} n 保留位数
 * @param {string} unit 单位--可选
 * @returns {string} 处理后的数值
 */

export const fomatFloat = (value: number, n: number, unit?: string):string => {
  const f = parseNum(Math.round(value * Math.pow(10, n)) / Math.pow(10, n))
  let s = f.toString()
  const rs = s.indexOf('.')
  if (rs < 0) {
    s += '.'
  }
  for (let i = s.length - s.indexOf('.'); i <= n; i++) {
    s += '0'
  }
  if (unit) {
    if (s.includes('-')) {
      return `-${unit}${s.substring(1)}`
    } else {
      return unit + s
    }
  }
  return s
}

/**
 * 节流函数
 * @param fn 需要节流的函数
 * @param wait 等待时间（毫秒）
 * @returns 节流后的函数
 */
export function throttle<T extends (...args: any[]) => any>(fn: T, delay: number) {
  let ticking = false;
  let lastArgs: any[] | null = null;

  const tick = () => {
    if (lastArgs) {
      fn(...lastArgs);
      lastArgs = null;
      requestAnimationFrame(() => {
        ticking = false;
      });
    } else {
      ticking = false;
    }
  };

  return function(this: any, ...args: Parameters<T>) {
    lastArgs = args;

    if (!ticking) {
      ticking = true;
      setTimeout(tick, delay);
    }
  };
}


/**
 * 计算周岁
 * @param birthDate 
 * @returns 
 */
export const getAge = (birthDate: string) => {
  const now = new Date()
  const birth = new Date(birthDate)
  let age = now.getFullYear() - birth.getFullYear()
  if (now.getMonth() < birth.getMonth() || (now.getMonth() === birth.getMonth() && now.getDate() < birth.getDate())) {
    age = age - 1
  }
  return age
}

/**
 * 根据身份证信息提取出生日期、年龄、性别
 * @param identity 身份证号
 * @returns 返回一个对象，包含出生日期、年龄、性别
 * @example
 * getIdentityInfo('11010119900307721X') // 返回 { birthDate: '1990-03-07', age: 30, gender: '男' }
 */
export const getIdentityInfo = (identity: string) => {
  const birthDate = identity.slice(6, 10) + '-' + identity.slice(10, 12) + '-' + identity.slice(12, 14)
  const age = getAge(birthDate)
  const gender = Number(identity.slice(-2, -1)) % 2 === 0 ? '2' : '1' // 1: 男，2: 女
  return { birthDate, age, gender }
}

// 银行卡格式化 每隔 4 个字符加一个空格
export const formatBankCard = (bankCard: string) => {
  return bankCard.replace(/(\d{4})(?=\d)/g, '$1 ')
}

// 银行卡脱敏 
export const desensitizeBankCard = (bankCard: string) => {
  const start = bankCard.slice(0, 4)
  const end = bankCard.slice(-4)
  const middle = '****'
  const bankCardNumber = `${start} ${middle} ${end}`
  return formatBankCard(bankCardNumber)
}

// file 转 base64
export const fileToBase64 = async (file: File) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => {
      resolve(reader.result)
    }
  })
}

// 简单合并两个对象
export const mergeObj = (obj1: Record<string, any>, obj2: Record<string, any>) => {

  const isUsefulValue = (value: any) => {
    if (value === undefined || value === null || value === '') {
      return false
    }
    if (value instanceof Array && value.length === 0) {
      return false
    }
    if (value instanceof Object && Object.keys(value).length === 0) {
      return false
    }
    return true
  }

  let result = {
    ...obj1,
  }

  for (let key in obj2) {
    if (isUsefulValue(obj2[key])) {
      result[key] = obj2[key]
    }
  }
  return result
}


export const delayTimer = (time: number = 2000) => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve(true)
    }, time)
  })
}

// 检查日期字符串是否为今天
export function isTodayDateStr(dateStr: string) {
  if (!dateStr) {
    return false
  }

  const safeDateStr = (typeof dateStr === 'string') 
    ? dateStr.replace(/-/g, '/') 
    : dateStr;

  const inputDate = new Date(safeDateStr);
  const today = new Date();

  if (isNaN(inputDate.getTime())) {
    console.error('Invalid date string:', dateStr)
    return false;
  }

  return (
    inputDate.getFullYear() === today.getFullYear() &&
    inputDate.getMonth() === today.getMonth() &&
    inputDate.getDate() === today.getDate()
  );
}